import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppService from '../../../../Configurations/Appservice';
import {
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';

// Components
import FinanceMetricCard from './components/FinanceMetricCard';
import BankAccountsModal from './components/BankAccountsModal';
import CustomerWalletBalancesModal from './components/CustomerWalletBalancesModal';
import PendingPaymentsModal from './components/PendingPaymentsModal';
import PendingCommissionsModal from './components/PendingCommissionsModal';
import RecordTaxPaymentDialog from './components/RecordTaxPaymentDialog';
import RecordOfflineTransactionDialog from '../ThaklisMasterWallet/RecordOfflineTransactionDialog';

// Mock Data
import { MOCK_BANK_ACCOUNTS, MOCK_CUSTOMER_WALLET_BALANCES, MOCK_ALL_TRANSACTIONS } from './mockData';

/**
 * Thaklis Finance Overview - Main Component
 * Phase 9: Connecting to real APIs
 */
const ThaklisFinanceOverview = () => {
  const navigate = useNavigate();

  // Modal states
  const [bankAccountsModalOpen, setBankAccountsModalOpen] = useState(false);
  const [customerWalletBalancesModalOpen, setCustomerWalletBalancesModalOpen] = useState(false);
  const [pendingPaymentsModalOpen, setPendingPaymentsModalOpen] = useState(false);
  const [pendingCommissionsModalOpen, setPendingCommissionsModalOpen] = useState(false);
  const [offlineTransactionDialogOpen, setOfflineTransactionDialogOpen] = useState(false);
  const [taxPaymentDialogOpen, setTaxPaymentDialogOpen] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Real data state
  const [overviewData, setOverviewData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Revenue & Profitability period filter. Only this section is date-scoped
  // (by ticket completion date); the Tax Overview section stays cumulative.
  // 'all' → use the all-time overviewData; otherwise revenueSection holds the
  // period-scoped numbers fetched from the revenue-tax slice with a date range.
  const [revenuePeriod, setRevenuePeriod] = useState('all');
  const [revenueSection, setRevenueSection] = useState({});
  const [revenueLoading, setRevenueLoading] = useState(false);


  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);

  // Customer wallets state
  const [customerWallets, setCustomerWallets] = useState([]);
  const [customerWalletsLoading, setCustomerWalletsLoading] = useState(false);


  // The single /overview endpoint was slow because it aggregated everything
  // serially. The dashboard is now backed by six independent slice endpoints
  // fetched in parallel; each tile renders as soon as its slice arrives.
  const SECTION_ENDPOINTS = [
    'v1/admin/finance/overview/bank-wallet',
    'v1/admin/finance/overview/customer-payments',
    'v1/admin/finance/overview/service-partner',
    'v1/admin/finance/overview/channel-partner',
    'v1/admin/finance/overview/ticket-payments',
    'v1/admin/finance/overview/revenue-tax',
  ];

  const fetchFinanceOverview = () => {
    setLoading(true);
    setError(null);
    // Clear stale data so refreshes don't show old numbers next to in-flight tiles.
    setOverviewData({});

    let remaining = SECTION_ENDPOINTS.length;
    let hadFailure = false;

    SECTION_ENDPOINTS.forEach((path) => {
      AppService.get(path)
        .then((response) => {
          if (response && typeof response === 'object') {
            // Merge this slice into whatever has already arrived.
            setOverviewData((prev) => ({ ...prev, ...response }));
          }
        })
        .catch((err) => {
          hadFailure = true;
          console.error(`Error fetching ${path}:`, err);
        })
        .finally(() => {
          remaining -= 1;
          if (remaining === 0) {
            setLoading(false);
            if (hadFailure) {
              setError('Some sections failed to load — refresh to retry.');
            }
          }
        });
    });
  };

  // Revenue & Profitability period presets.
  const REVENUE_PERIODS = [
    { key: 'all', label: 'All time' },
    { key: 'thisMonth', label: 'This month' },
    { key: 'last3months', label: 'Last 3 months' },
    { key: 'last6months', label: 'Last 6 months' },
    { key: 'thisYear', label: 'This year' },
  ];

  const fmtDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Resolve a preset to an inclusive { startDate, endDate } range (yyyy-MM-dd).
  const computeRevenueRange = (preset) => {
    const today = new Date();
    let start;
    switch (preset) {
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'last3months':
        start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        break;
      case 'last6months':
        start = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
        break;
      case 'thisYear':
        start = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        return null;
    }
    return { startDate: fmtDate(start), endDate: fmtDate(today) };
  };

  // Fetch the period-scoped Revenue & Profitability numbers for a preset.
  const fetchRevenueSection = (preset) => {
    const range = computeRevenueRange(preset);
    if (!range) {
      setRevenueSection({});
      return;
    }
    setRevenueLoading(true);
    AppService.get(
      `v1/admin/finance/overview/revenue-tax?startDate=${range.startDate}&endDate=${range.endDate}`
    )
      .then((response) => {
        if (response && typeof response === 'object') {
          setRevenueSection(response);
        }
      })
      .catch((err) => {
        console.error('Error fetching revenue period:', err);
        setRevenueSection({});
      })
      .finally(() => setRevenueLoading(false));
  };

  const handleRevenuePeriodChange = (preset) => {
    setRevenuePeriod(preset);
    if (preset === 'all') {
      setRevenueSection({});
      return;
    }
    fetchRevenueSection(preset);
  };

  // Fetch bank accounts
  const fetchBankAccounts = async () => {
    setBankAccountsLoading(true);
    try {
      const response = await AppService.get('v1/admin/finance/bank-accounts');

      const accounts = response.map(acc => ({
        id: acc.id,
        bankName: acc.bankName,
        type: acc.type,
        accountNumber: acc.accountNumber,
        iban: acc.iban,
        swiftCode: acc.swiftCode,
        balance: acc.balance,
        status: acc.status
      }));

      setBankAccounts(accounts);
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
      // Fall back to mock data on error
      setBankAccounts(MOCK_BANK_ACCOUNTS);
    } finally {
      setBankAccountsLoading(false);
    }
  };

  // Fetch customer wallets from API
  const fetchCustomerWallets = async () => {
    setCustomerWalletsLoading(true);
    try {
      const response = await AppService.get('v1/admin/finance/customer-wallets');

      setCustomerWallets(response);
    } catch (err) {
      console.error('Error fetching customer wallets:', err);
      // Fall back to mock data on error
      setCustomerWallets(MOCK_CUSTOMER_WALLET_BALANCES);
    } finally {
      setCustomerWalletsLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchFinanceOverview();
    fetchBankAccounts();
    fetchCustomerWallets();
  }, []);

  // Refresh handler
  const handleRefresh = () => {
    fetchFinanceOverview();
    fetchBankAccounts();
    fetchCustomerWallets();
    if (revenuePeriod !== 'all') {
      fetchRevenueSection(revenuePeriod);
    }
  };

  const formatAmount = (amount) => {
    const value = amount || 0;
    return `AED ${value.toLocaleString('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Section 1 reads period-scoped numbers when a period is selected; otherwise
  // the all-time slice. The Tax Overview section always uses overviewData.
  const revenue = revenuePeriod === 'all' ? overviewData : revenueSection;

  return (
    <Box sx={{ px: 3, py: 3 }}>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <AccountBalanceIcon sx={{ fontSize: '1.5rem', color: 'text.secondary' }} />
          <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
            Finance Overview
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Unified financial dashboard with all metrics
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: 'error.50',
            borderLeft: 4,
            borderColor: 'error.main',
          }}
        >
          <Typography variant="body2" color="error.dark" fontWeight="medium">
            {error}
          </Typography>
        </Paper>
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4} mb={3}>
          <CircularProgress />
          <Typography variant="body1" ml={2} color="text.secondary">
            Loading finance data...
          </Typography>
        </Box>
      )}

      {/* Metric Section : Top Financial Metrics (5 Cards) */}
      <Grid container spacing={2} mb={4} sx={{ alignItems: 'stretch' }}>
        {/* Card 1: Total Bank Balance */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            sx={{
              p: 2,
              height: '100%',
              minHeight: 120,
              background: 'linear-gradient(135deg, #1a7f8f 0%, #156b7b 100%)',
              color: 'white',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px rgba(26, 127, 143, 0.3)',
              },
            }}
            onClick={() => setBankAccountsModalOpen(true)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', height: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 1 }}>
                  Total Bank Balance
                </Typography>
                <Box>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
                    {formatAmount(overviewData.totalBankBalance)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75, mt: 0.5 }}>
                    {overviewData.activeBankAccounts || 0} active accounts
                  </Typography>
                </Box>
              </Box>
              <AccountBalanceIcon sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
            </Box>
          </Paper>
        </Grid>

        {/* Card 2: Ticket Payments (Paid + Pending) */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            sx={{
              p: 2,
              height: '100%',
              minHeight: 120,
              background: 'linear-gradient(135deg, #1f6b7a 0%, #1a5f6b 100%)',
              color: 'white',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px rgba(26, 95, 107, 0.3)',
              },
            }}
            onClick={() => navigate('/admin/account-management/payments')}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ width: '100%' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 0.6 }}>
                  Ticket Payments
                </Typography>

                {/* Paid Section */}
                <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#4caf50' }}>
                        {formatAmount(overviewData.paidTicketPaymentsAmount)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                        (Paid)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                      {overviewData.paidTicketCount || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* In Review Section — WAITING_FOR_APPROVAL: online payment
                    mid-gateway, distinct from settled and not-yet-initiated. */}
                <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#42a5f5' }}>
                        {formatAmount(overviewData.inReviewTicketPaymentsAmount)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                        (Processing)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                      {overviewData.inReviewTicketCount || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* Pending Section */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ff9800' }}>
                        {formatAmount(overviewData.pendingTicketPaymentsAmount)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                        (Pending)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                      {overviewData.pendingTicketCount || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <PaymentIcon sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
            </Box>
          </Paper>
        </Grid>

        {/* Card 3: Customer Wallet Balances */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            sx={{
              p: 2,
              height: '100%',
              minHeight: 120,
              background: 'linear-gradient(135deg, #1f6b7a 0%, #1a5f6b 100%)',
              color: 'white',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px rgba(26, 95, 107, 0.3)',
              },
            }}
            onClick={() => setCustomerWalletBalancesModalOpen(true)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', height: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 1 }}>
                  Customer Wallet Balances
                </Typography>
                <Box>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
                    {formatAmount(overviewData.totalCustomerWalletBalances)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75, mt: 0.5 }}>
                    {overviewData.customersWithBalance || 0} customers with balance
                  </Typography>
                </Box>
              </Box>
              <AccountBalanceWalletIcon sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
            </Box>
          </Paper>
        </Grid>

        {/* Card 4: Service Partner Payments (Paid + Pending) */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            sx={{
              p: 2,
              background: 'linear-gradient(135deg, #1f6b7a 0%, #1a5f6b 100%)',
              color: 'white',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px rgba(26, 95, 107, 0.3)',
              },
            }}
            onClick={() => setPendingPaymentsModalOpen(true)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ width: '100%' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 0.6 }}>
                  Partner Payments
                </Typography>

                {/* Paid Section */}
                <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#4caf50' }}>
                        {formatAmount(overviewData.paidServicePartnerAmount)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                        (Paid)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                      {overviewData.paidServicePartnerCount || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* In Review Section */}
                <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#03a9f4' }}>
                        {formatAmount(overviewData.pendingApprovalServicePartnerAmount)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                        (In Review)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                      {overviewData.pendingApprovalServicePartnerCount || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* Pending Section */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ff9800' }}>
                        {formatAmount(overviewData.pendingServicePartnerPayments)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                        (Pending)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                      {overviewData.pendingServicePartnerCount || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <AttachMoneyIcon sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
            </Box>
          </Paper>
        </Grid>

        {/* Card 5: Channel Partner Commissions (Paid + Pending) */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            sx={{
              p: 2,
              background: 'linear-gradient(135deg, #155566 0%, #0f4656 100%)',
              color: 'white',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px rgba(21, 85, 102, 0.3)',
              },
            }}
            onClick={() => setPendingCommissionsModalOpen(true)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ width: '100%' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 0.6 }}>
                  Partner Commissions
                </Typography>

                {/* Paid Section */}
                <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#4caf50' }}>
                        {formatAmount(overviewData.paidChannelPartnerAmount)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                        (Paid)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                      {overviewData.paidChannelPartnerCount || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* In Review Section */}
                <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#03a9f4' }}>
                        {formatAmount(overviewData.pendingApprovalChannelPartnerAmount)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                        (In Review)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                      {overviewData.pendingApprovalChannelPartnerCount || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* Pending Section */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ff9800' }}>
                        {formatAmount(overviewData.pendingChannelPartnerCommissions)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                        (Pending)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                      {overviewData.pendingChannelPartnerCount || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <ReceiptIcon sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Section 1: Revenue & Profitability */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="h6" fontWeight="bold">
            Revenue &amp; Profitability
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {revenueLoading && <CircularProgress size={16} />}
            {REVENUE_PERIODS.map(({ key, label }) => {
              const active = revenuePeriod === key;
              return (
                <Button
                  key={key}
                  size="small"
                  disableElevation
                  variant={active ? 'contained' : 'outlined'}
                  onClick={() => handleRevenuePeriodChange(key)}
                  sx={{
                    fontSize: '0.72rem',
                    py: 0.3,
                    px: 1.2,
                    textTransform: 'none',
                    minWidth: 'auto',
                    fontWeight: active ? 600 : 500,
                    color: active ? '#ffffff' : '#1a2b4a',
                    bgcolor: active ? '#1a2b4a' : 'transparent',
                    borderColor: active ? '#1a2b4a' : 'rgba(26, 43, 74, 0.4)',
                    '&:hover': {
                      bgcolor: active ? '#16243d' : 'rgba(26, 43, 74, 0.08)',
                      borderColor: '#1a2b4a',
                    },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
        </Box>
        {revenuePeriod !== 'all' && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Figures below cover tickets created in the selected period. Tax Overview remains cumulative.
          </Typography>
        )}

        <Grid container spacing={1} mt={1}>
          <Grid item xs={12} sm={6} md={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Revenue
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                {formatAmount(revenue.totalRevenue)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Govt Fee: {formatAmount(revenue.totalGovtFee)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Service Charge: {formatAmount(revenue.totalServiceCharge)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Profit
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {formatAmount(revenue.profitToThaklis)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Pending Profit
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                {formatAmount(revenue.pendingProfitToThaklis)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Received Profit
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {formatAmount(revenue.receivedProfitToThaklis)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Profit on customer-paid tickets
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Tax on Profit
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="error.main">
                {formatAmount(revenue.taxOnProfit)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                5% of received profit
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Net Profit
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {formatAmount(revenue.netProfit)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Received Profit − Tax
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Section 2: Thaklis Master Wallet */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <AccountBalanceWalletIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Thaklis Master Wallet
          </Typography>
        </Box>

        <Typography variant="h4" color="primary.main" fontWeight="bold" gutterBottom>
          {formatAmount(overviewData.totalBankBalance)}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Available funds for operations
        </Typography>

        <Box display="flex" gap={2} flexWrap="wrap">
          <Button variant="contained" color="primary">
            RECORD EXPENSE
          </Button>
          <Button variant="contained" color="secondary">
            BANK TRANSFER
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={() => setOfflineTransactionDialogOpen(true)}
          >
            RECORD OFFLINE TX
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<Chip label={overviewData.pendingCustomerPaymentCount} size="small" color="error" />}
            onClick={() => navigate('/admin/finance/payment-approvals')}
          >
            CUSTOMER PAYMENT APPROVALS
          </Button>
        </Box>
      </Paper>

      {/* Record Offline Transaction Dialog */}
      <RecordOfflineTransactionDialog
        open={offlineTransactionDialogOpen}
        onClose={() => setOfflineTransactionDialogOpen(false)}
        onSuccess={() => {
          setOfflineTransactionDialogOpen(false);
          // Optionally refresh data
        }}
        bankAccounts={bankAccounts.map(b => ({
          id: b.id,
          accountName: b.accountName || b.name || b.bankName,
          bankName: b.bankName
        }))}
      />

      {/* Section 3: Tax Overview (Placeholder - Coming Soon) */}
      <Paper sx={{ p: 3, mb: 3, position: 'relative' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon color="warning" />
            <Typography variant="h6" fontWeight="bold">
              Tax Overview
            </Typography>
          </Box>
          {/* Tax Overview is now live */}
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          5% UAE VAT on Thaklis profit. Tax payments to the UAE FTA reduce the outstanding liability.
        </Typography>

        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Tax on Profit
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="error.main">
                {formatAmount(overviewData.taxOnProfit)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                5% of received profit
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Tax Paid to Government
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                {formatAmount(overviewData.taxPaidToGovernment)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {overviewData.taxTransactionCount} payments to UAE FTA
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Tax Liability (Outstanding)
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                {formatAmount(overviewData.taxLiability)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tax on Profit − Paid
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Actions
              </Typography>
              <Box mt={1}>
                <Button variant="contained" size="small" onClick={() => setTaxPaymentDialogOpen(true)}
                  sx={{ bgcolor: '#1a7f8f', color: '#ffffff', '&:hover': { bgcolor: '#156b7b' }, maxWidth: 200 }}>
                  Record Tax Payment
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Section 5: Transaction Tabs */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          All Transactions
        </Typography>

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
          <Tab label="All Transactions" />
          <Tab label="Service Partner Payments (2)" />
          <Tab label="Commission Payments (2)" />
          <Tab label="Expenses" />
          <Tab label="Bank Reconciliation" />
        </Tabs>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Reference</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {MOCK_ALL_TRANSACTIONS.map((transaction, index) => (
                <TableRow key={index} hover>
                  <TableCell>{transaction.date}</TableCell>
                  <TableCell>
                    <Chip label={transaction.badge} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{transaction.description}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {transaction.reference}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                    >
                      {transaction.amount > 0 ? '+' : ''}
                      {formatAmount(Math.abs(transaction.amount))}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={transaction.status}
                      size="small"
                      color={getStatusColor(transaction.status)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modals */}
      <BankAccountsModal
        open={bankAccountsModalOpen}
        onClose={() => setBankAccountsModalOpen(false)}
        bankAccounts={bankAccounts}
      />

      <CustomerWalletBalancesModal
        open={customerWalletBalancesModalOpen}
        onClose={() => setCustomerWalletBalancesModalOpen(false)}
        walletBalances={customerWallets}
        summary={{
          totalBalance: overviewData.totalCustomerWalletBalances,
          customersWithBalance: overviewData.customersWithBalance,
          averageBalance: overviewData.averageWalletBalance,
        }}
      />

      <RecordTaxPaymentDialog
        open={taxPaymentDialogOpen}
        onClose={() => setTaxPaymentDialogOpen(false)}
        onSuccess={() => {
          setTaxPaymentDialogOpen(false);
          fetchFinanceOverview();
        }}
        maxAmount={Number(overviewData.taxLiability) || 0}
      />

    </Box>
  );
};

export default ThaklisFinanceOverview;
